import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { EditableNumberCell } from '@/components/financials/EditableNumberCell';
import { MetricCard } from '@/components/financials/MetricCard';
import { CoaCategorySelect } from '@/components/financials/CoaCategorySelect';
import { formatCurrency } from '@/lib/formatters';
import {
  buildMonthRange,
  buildMonthRangeForPeriod,
  currentYear,
  formatMonthLabel,
  toMonthKey,
  type PeriodPreset,
} from '@/lib/months';
import {
  useBulkUpsertProjectionPnl,
  useDeleteProjectionPayroll,
  useDeleteProjectionPayrollSalary,
  useInsertProjectionPayroll,
  useInsertProjectionPayrollSalary,
  useProjectionsPayroll,
  useProjectionsPayrollSalaries,
  useUpdateProjectionPayroll,
  useUpdateProjectionPayrollSalary,
} from '@/features/projections/hooks';
import type { Tables } from '@/types/database';

type PayrollRow = Tables<'projections_payroll'> & {
  profiles: Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email'> | null;
};
type SalaryRow = Tables<'projections_payroll_salaries'>;

const EMPLOYMENT_TYPES = [
  { value: 'fulltime', label: 'Full-time' },
  { value: 'parttime', label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'open', label: 'Open / Planned' },
] as const;

type RangeOption = 'year0' | 'year1' | 'year2' | '12' | '18' | '24';
const PERIOD_PRESETS: ReadonlySet<RangeOption> = new Set(['year0', 'year1', 'year2']);

const CURRENT_MONTH = toMonthKey(new Date());

/**
 * Employee Payroll model. Each employee is a row in `projections_payroll`;
 * their base salary lives in `projections_payroll_salaries` as a series of
 * effective-dated records so a raise or pay-cut is just a new record. The
 * grid shows, per month, the salary/wage cost and the employer-tax cost,
 * only for months the employee is active.
 *
 * "Auto-fill to P&L" pushes the total monthly cost into a chosen P&L expense
 * account for every non-historic month (current month forward), so the
 * projected P&L reflects the headcount plan without double-keying.
 */
export function EmployeePayrollTab() {
  const { data, isLoading } = useProjectionsPayroll();
  const { data: salaries, isLoading: salLoading } = useProjectionsPayrollSalaries();
  const insert = useInsertProjectionPayroll();
  const insertSalary = useInsertProjectionPayrollSalary();
  const bulkPnl = useBulkUpsertProjectionPnl();

  const [range, setRange] = useState<RangeOption>('year0');
  const months = useMemo(() => {
    if (PERIOD_PRESETS.has(range)) return buildMonthRangeForPeriod(range as PeriodPreset);
    return buildMonthRange({ from: 'future', count: Number(range) });
  }, [range]);

  const [draftName, setDraftName] = useState('');
  const [draftRole, setDraftRole] = useState('');
  const [draftType, setDraftType] = useState('fulltime');
  const [draftStart, setDraftStart] = useState('');
  const [draftSalary, setDraftSalary] = useState('');

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [fillAccount, setFillAccount] = useState<string | null>(null);

  /** payroll_id → sorted salary records (ascending by effective_date). */
  const salariesByEmp = useMemo(() => {
    const m = new Map<string, SalaryRow[]>();
    for (const s of salaries ?? []) {
      const arr = m.get(s.payroll_id) ?? [];
      arr.push(s);
      m.set(s.payroll_id, arr);
    }
    for (const arr of m.values()) arr.sort((a, b) => a.effective_date.localeCompare(b.effective_date));
    return m;
  }, [salaries]);

  const employees = useMemo(() => (data ?? []) as PayrollRow[], [data]);

  /** Monthly (wage, tax) for an employee in a given month, 0 when inactive. */
  const monthlyCost = useMemo(() => {
    return (emp: PayrollRow, month: string): { wage: number; tax: number } => {
      const end = monthEnd(month);
      const active = emp.start_date <= end && (!emp.end_date || emp.end_date >= month);
      if (!active) return { wage: 0, tax: 0 };
      const annual = effectiveAnnualCents(emp, salariesByEmp.get(emp.id) ?? [], end);
      const baseMonthly = annual / 12;
      const bonusMonthly = emp.bonus_annual_cents / 12;
      const taxBase = baseMonthly + bonusMonthly;
      const tax = taxBase * (Number(emp.employer_taxes_pct) / 100);
      const wage = baseMonthly + bonusMonthly + emp.benefits_monthly_cents;
      return { wage: Math.round(wage), tax: Math.round(tax) };
    };
  }, [salariesByEmp]);

  /** Per-month total cost across all employees (wage + tax). */
  const monthTotals = useMemo(() => {
    const out = new Map<string, number>();
    for (const m of months) {
      let total = 0;
      for (const emp of employees) {
        const { wage, tax } = monthlyCost(emp, m);
        total += wage + tax;
      }
      out.set(m, total);
    }
    return out;
  }, [months, employees, monthlyCost]);

  const headlineTotals = useMemo(() => {
    const currentCost = monthTotals.get(CURRENT_MONTH) ?? monthlyCostNow(employees, salariesByEmp);
    const headcount = employees.filter(
      (r) => (!r.end_date || r.end_date >= today()) && r.start_date <= monthEnd(CURRENT_MONTH),
    ).length;
    return { monthlyTotal: currentCost, annualTotal: currentCost * 12, headcount };
  }, [monthTotals, employees, salariesByEmp]);

  const canSubmit = draftName && draftStart;
  const handleAdd = () => {
    if (!canSubmit) return;
    const dollars = Number(draftSalary.replace(/[$,_\s]/g, '')) || 0;
    insert.mutate(
      {
        full_name: draftName,
        role_title: draftRole || null,
        employment_type: draftType,
        start_date: draftStart,
        base_salary_annual_cents: Math.round(dollars * 100),
        employer_taxes_pct: 8.65,
      },
      {
        onSuccess: (created) => {
          // Seed the salary history with the starting salary so the
          // month-by-month outline has something to read immediately.
          insertSalary.mutate({
            payroll_id: created.id,
            effective_date: draftStart,
            base_salary_annual_cents: Math.round(dollars * 100),
          });
          setDraftName('');
          setDraftRole('');
          setDraftType('fulltime');
          setDraftStart('');
          setDraftSalary('');
        },
      },
    );
  };

  const handleAutoFill = () => {
    if (!fillAccount) {
      toast.error('Pick a P&L expense account first');
      return;
    }
    const cells = months
      .filter((m) => m >= CURRENT_MONTH)
      .map((m) => ({
        account_id: fillAccount,
        month: m,
        amount_cents: monthTotals.get(m) ?? 0,
      }));
    if (cells.length === 0) {
      toast.error('No future months in the selected range');
      return;
    }
    bulkPnl.mutate(
      { cells },
      {
        onSuccess: (n) => toast.success(`Pushed ${n} month${n === 1 ? '' : 's'} of payroll into the P&L`),
        onError: (e) => toast.error(e instanceof Error ? e.message : 'Auto-fill failed'),
      },
    );
  };

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const busy = isLoading || salLoading;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <MetricCard label="Active headcount" value={String(headlineTotals.headcount)} loading={busy} />
        <MetricCard label="This month's cost" value={formatCurrency(headlineTotals.monthlyTotal)} loading={busy} />
        <MetricCard label="Annualized" value={formatCurrency(headlineTotals.annualTotal)} loading={busy} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add employee</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <Input
              placeholder="Full name"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="md:col-span-2"
              aria-label="Full name"
            />
            <Input
              placeholder="Role title"
              value={draftRole}
              onChange={(e) => setDraftRole(e.target.value)}
              aria-label="Role title"
            />
            <Select value={draftType} onValueChange={setDraftType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={draftStart}
              onChange={(e) => setDraftStart(e.target.value)}
              aria-label="Start date"
            />
            <div className="flex gap-2">
              <Input
                placeholder="Annual salary"
                inputMode="decimal"
                value={draftSalary}
                onChange={(e) => setDraftSalary(e.target.value)}
                aria-label="Annual salary"
              />
              <Button onClick={handleAdd} disabled={!canSubmit || insert.isPending} size="icon">
                <Plus className="h-4 w-4" strokeWidth={2} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Cost outline</CardTitle>
          </div>
          <Select value={range} onValueChange={(v) => setRange(v as RangeOption)}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="year0">{currentYear()}</SelectItem>
              <SelectItem value="year1">{currentYear() + 1}</SelectItem>
              <SelectItem value="year2">{currentYear() + 2}</SelectItem>
              <SelectSeparator />
              <SelectItem value="12">Next 12 months</SelectItem>
              <SelectItem value="18">Next 18 months</SelectItem>
              <SelectItem value="24">Next 24 months</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="overflow-x-auto px-0 pb-6">
          {busy ? (
            <Skeleton className="mx-6 h-32" />
          ) : employees.length === 0 ? (
            <p className="px-6 text-sm text-muted-foreground">No employees yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-border">
                <tr className="text-left">
                  <th className="sticky left-0 z-10 bg-card px-4 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Employee
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Current salary
                  </th>
                  {months.map((m) => (
                    <th
                      key={m}
                      className="min-w-[88px] px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-muted-foreground tabular-nums"
                    >
                      {formatMonthLabel(m)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <EmployeeRows
                    key={emp.id}
                    emp={emp}
                    months={months}
                    salaries={salariesByEmp.get(emp.id) ?? []}
                    monthlyCost={monthlyCost}
                    expanded={expanded.has(emp.id)}
                    onToggle={() => toggle(emp.id)}
                  />
                ))}
              </tbody>
              <tfoot className="border-t border-border bg-secondary/30 font-medium">
                <tr>
                  <td className="sticky left-0 z-10 bg-secondary/30 px-4 py-3">Total cost</td>
                  <td />
                  {months.map((m) => (
                    <td key={m} className="px-3 py-3 text-right tabular-nums">
                      {formatCurrency(monthTotals.get(m) ?? 0)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Auto-fill the projected P&amp;L</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[18rem] flex-1">
              <CoaCategorySelect
                value={fillAccount}
                onChange={setFillAccount}
                placeholder="Choose payroll expense account"
              />
            </div>
            <Button onClick={handleAutoFill} disabled={bulkPnl.isPending || employees.length === 0}>
              <Sparkles className="h-4 w-4" />
              Auto-fill to P&amp;L
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface EmployeeRowsProps {
  emp: PayrollRow;
  months: string[];
  salaries: SalaryRow[];
  monthlyCost: (emp: PayrollRow, month: string) => { wage: number; tax: number };
  expanded: boolean;
  onToggle: () => void;
}

function EmployeeRows({ emp, months, salaries, monthlyCost, expanded, onToggle }: EmployeeRowsProps) {
  const currentSalary = effectiveAnnualCents(emp, salaries, monthEnd(CURRENT_MONTH));

  return (
    <>
      <tr className="border-b border-border hover:bg-secondary/30">
        <td className="sticky left-0 z-0 bg-card px-4 py-2.5">
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-2 text-left font-medium text-foreground"
          >
            {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <span>
              {emp.full_name}
              {emp.role_title ? (
                <span className="ml-2 text-xs font-normal text-muted-foreground">{emp.role_title}</span>
              ) : null}
            </span>
          </button>
        </td>
        <td className="px-4 py-2.5 text-right tabular-nums">{formatCurrency(currentSalary)}</td>
        {months.map((m) => {
          const { wage, tax } = monthlyCost(emp, m);
          if (wage === 0 && tax === 0) {
            return (
              <td key={m} className="px-3 py-2.5 text-right text-muted-foreground/40 tabular-nums">
                —
              </td>
            );
          }
          return (
            <td key={m} className="px-3 py-2.5 text-right tabular-nums">
              <div className="text-foreground">{formatCurrency(wage)}</div>
              <div className="text-[11px] text-muted-foreground">+{formatCurrency(tax)} tax</div>
            </td>
          );
        })}
      </tr>
      {expanded && (
        <tr className="border-b border-border bg-secondary/20">
          <td colSpan={months.length + 2} className="px-4 py-4">
            <EmployeeDetail emp={emp} salaries={salaries} />
          </td>
        </tr>
      )}
    </>
  );
}

function EmployeeDetail({ emp, salaries }: { emp: PayrollRow; salaries: SalaryRow[] }) {
  const update = useUpdateProjectionPayroll();
  const del = useDeleteProjectionPayroll();
  const insertSalary = useInsertProjectionPayrollSalary();
  const updateSalary = useUpdateProjectionPayrollSalary();
  const deleteSalary = useDeleteProjectionPayrollSalary();

  const [newDate, setNewDate] = useState('');
  const [newAmount, setNewAmount] = useState('');

  const handleAddSalary = () => {
    const dollars = Number(newAmount.replace(/[$,_\s]/g, '')) || 0;
    if (!newDate || dollars <= 0) {
      toast.error('Enter an effective date and salary');
      return;
    }
    insertSalary.mutate(
      {
        payroll_id: emp.id,
        effective_date: newDate,
        base_salary_annual_cents: Math.round(dollars * 100),
      },
      {
        onSuccess: () => {
          setNewDate('');
          setNewAmount('');
        },
      },
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Salary history</h4>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1">Effective</th>
              <th className="py-1 text-right">Annual base</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {salaries.length === 0 && (
              <tr>
                <td colSpan={3} className="py-2 text-muted-foreground">
                  No salary records — using {formatCurrency(emp.base_salary_annual_cents)} from{' '}
                  {emp.start_date}.
                </td>
              </tr>
            )}
            {salaries.map((s) => (
              <tr key={s.id} className="border-t border-border/60">
                <td className="py-1.5">
                  <input
                    type="date"
                    defaultValue={s.effective_date}
                    onBlur={(e) => {
                      if (e.target.value && e.target.value !== s.effective_date)
                        updateSalary.mutate({ id: s.id, effective_date: e.target.value });
                    }}
                    className="bg-transparent text-sm focus:outline-none"
                    aria-label="Effective date"
                  />
                </td>
                <td className="py-1.5 text-right">
                  <EditableNumberCell
                    value={s.base_salary_annual_cents / 100}
                    ariaLabel="Annual base"
                    onCommit={(d) =>
                      updateSalary.mutate({ id: s.id, base_salary_annual_cents: Math.round(d * 100) })
                    }
                  />
                </td>
                <td className="py-1.5">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => deleteSalary.mutate(s.id)}
                    aria-label="Delete salary record"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                  </Button>
                </td>
              </tr>
            ))}
            <tr className="border-t border-border/60">
              <td className="py-1.5">
                <Input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  className="h-8"
                  aria-label="New effective date"
                />
              </td>
              <td className="py-1.5">
                <Input
                  placeholder="Annual"
                  inputMode="decimal"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  className="h-8 text-right"
                  aria-label="New annual base"
                />
              </td>
              <td className="py-1.5">
                <Button size="icon" variant="ghost" onClick={handleAddSalary} aria-label="Add salary record">
                  <Plus className="h-4 w-4" strokeWidth={2} />
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="space-y-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Details</h4>
        <div className="grid grid-cols-2 gap-3">
          <DetailField label="Role">
            <input
              defaultValue={emp.role_title ?? ''}
              placeholder="—"
              onBlur={(e) => {
                const next = e.target.value || null;
                if (next !== emp.role_title) update.mutate({ id: emp.id, role_title: next });
              }}
              className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus:outline-none"
              aria-label="Role"
            />
          </DetailField>
          <DetailField label="Employment type">
            <Select
              value={emp.employment_type}
              onValueChange={(v) => update.mutate({ id: emp.id, employment_type: v })}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMPLOYMENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DetailField>
          <DetailField label="Start date">
            <input
              type="date"
              defaultValue={emp.start_date}
              onBlur={(e) => {
                if (e.target.value && e.target.value !== emp.start_date)
                  update.mutate({ id: emp.id, start_date: e.target.value });
              }}
              className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus:outline-none"
              aria-label="Start date"
            />
          </DetailField>
          <DetailField label="End date">
            <input
              type="date"
              defaultValue={emp.end_date ?? ''}
              onBlur={(e) => {
                const next = e.target.value || null;
                if (next !== emp.end_date) update.mutate({ id: emp.id, end_date: next });
              }}
              className="w-full rounded border border-input bg-card px-2 py-1.5 text-sm focus:outline-none"
              aria-label="End date"
            />
          </DetailField>
          <DetailField label="Employer taxes %">
            <EditableNumberCell
              value={Number(emp.employer_taxes_pct)}
              format="pct"
              ariaLabel="Employer taxes percent"
              onCommit={(pct) => update.mutate({ id: emp.id, employer_taxes_pct: pct })}
              className="rounded border border-input"
            />
          </DetailField>
          <DetailField label="Benefits / mo">
            <EditableNumberCell
              value={emp.benefits_monthly_cents / 100}
              ariaLabel="Benefits monthly"
              onCommit={(d) => update.mutate({ id: emp.id, benefits_monthly_cents: Math.round(d * 100) })}
              className="rounded border border-input"
            />
          </DetailField>
          <DetailField label="Annual bonus">
            <EditableNumberCell
              value={emp.bonus_annual_cents / 100}
              ariaLabel="Bonus annual"
              onCommit={(d) => update.mutate({ id: emp.id, bonus_annual_cents: Math.round(d * 100) })}
              className="rounded border border-input"
            />
          </DetailField>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-fresh-salmon-dark"
          onClick={() => {
            if (confirm(`Remove ${emp.full_name} from the payroll model?`)) del.mutate(emp.id);
          }}
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.6} />
          Remove employee
        </Button>
      </div>
    </div>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Latest effective annual base (cents) on/before `asOf` (YYYY-MM-DD). Falls
 *  back to the payroll row's own base_salary_annual_cents if there are no
 *  salary records yet (and the employee has started by then). */
function effectiveAnnualCents(
  emp: Tables<'projections_payroll'>,
  salaries: Tables<'projections_payroll_salaries'>[],
  asOf: string,
): number {
  let chosen: number | null = null;
  for (const s of salaries) {
    if (s.effective_date <= asOf) chosen = s.base_salary_annual_cents;
    else break; // salaries are sorted ascending
  }
  if (chosen != null) return chosen;
  if (emp.start_date <= asOf) return emp.base_salary_annual_cents;
  return 0;
}

function monthlyCostNow(
  employees: Tables<'projections_payroll'>[],
  salariesByEmp: Map<string, Tables<'projections_payroll_salaries'>[]>,
): number {
  const end = monthEnd(CURRENT_MONTH);
  let total = 0;
  for (const emp of employees) {
    const active = emp.start_date <= end && (!emp.end_date || emp.end_date >= CURRENT_MONTH);
    if (!active) continue;
    const annual = effectiveAnnualCents(emp, salariesByEmp.get(emp.id) ?? [], end);
    const baseMonthly = annual / 12;
    const bonusMonthly = emp.bonus_annual_cents / 12;
    const tax = (baseMonthly + bonusMonthly) * (Number(emp.employer_taxes_pct) / 100);
    total += Math.round(baseMonthly + bonusMonthly + emp.benefits_monthly_cents + tax);
  }
  return total;
}

/** Last day of `monthIso` (YYYY-MM-01) as YYYY-MM-DD, in UTC. */
function monthEnd(monthIso: string): string {
  const d = new Date(`${monthIso}T00:00:00Z`);
  const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
  return `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, '0')}-${String(
    last.getUTCDate(),
  ).padStart(2, '0')}`;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
